// Wave 3B — Commit 3ab
// Bottom sheet listing every tier_1 + tier_2 stockist for a single item.
// Tapping a store row opens Google Maps for directions.

import { MapPin, X } from "lucide-react";
import type {
  OfflineShoppingStore,
  OutfitItemOfflineShopping,
} from "@/hooks/useOutfitItems";

interface StoresExpandedSheetProps {
  open: boolean;
  onClose: () => void;
  itemName?: string | null;
  brand?: string | null;
  item: Partial<OutfitItemOfflineShopping> | null;
}

function metresToMiles(m: number | null | undefined): number | null {
  if (m == null || !Number.isFinite(m)) return null;
  return m / 1609.34;
}

function formatDistance(store: OfflineShoppingStore): string {
  const mi =
    typeof store.distance_mi === "number"
      ? store.distance_mi
      : metresToMiles(store.distance_m ?? null);
  if (mi == null) return "nearby";
  if (mi < 0.1) return "< 0.1 mi";
  return `${mi.toFixed(1)} mi`;
}

function mapsHref(store: OfflineShoppingStore): string {
  if (store.place_id) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.name)}&query_place_id=${encodeURIComponent(store.place_id)}`;
  }
  const q =
    store.address ??
    (store.lat != null && store.lng != null
      ? `${store.lat},${store.lng}`
      : store.name);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export function StoresExpandedSheet({
  open,
  onClose,
  itemName,
  brand,
  item,
}: StoresExpandedSheetProps) {
  if (!open) return null;

  const tier1 = Array.isArray(item?.offline_shopping_tier_1)
    ? [...item!.offline_shopping_tier_1!]
    : [];
  const tier2 = Array.isArray(item?.offline_shopping_tier_2)
    ? [...item!.offline_shopping_tier_2!]
    : [];
  const sortByDist = (a: OfflineShoppingStore, b: OfflineShoppingStore) =>
    (a.distance_m ?? Infinity) - (b.distance_m ?? Infinity);
  tier1.sort(sortByDist);
  tier2.sort(sortByDist);

  const sections: Array<{
    key: string;
    label: string;
    rows: OfflineShoppingStore[];
  }> = [];
  if (tier1.length > 0) sections.push({ key: "tier_1", label: "Brand stores", rows: tier1 });
  if (tier2.length > 0)
    sections.push({ key: "tier_2", label: "Also stocked at", rows: tier2 });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Nearby stores"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 200,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 560,
          background: "#FFFCF8",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: 20,
          paddingBottom: "calc(20px + var(--safe-bottom, 0px))",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#2C2820" }}>
              {brand ?? "Nearby stores"}
            </div>
            {itemName && (
              <div style={{ fontSize: 12, color: "#6B6560", marginTop: 2 }}>
                {itemName}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              padding: 4,
              cursor: "pointer",
              color: "#6B6560",
            }}
          >
            <X size={20} />
          </button>
        </div>

        {sections.length === 0 && (
          <div style={{ fontSize: 13, color: "#6B6560", padding: "12px 0" }}>
            No nearby stores found for this piece.
          </div>
        )}

        {sections.map((section) => (
          <div key={section.key} style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#9B958E",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                marginBottom: 6,
              }}
            >
              {section.label}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {section.rows.map((s, i) => (
                <a
                  key={`${section.key}-${s.place_id ?? i}`}
                  href={mapsHref(s)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid #F1EFE8",
                    background: "#FFFFFF",
                    textDecoration: "none",
                    color: "#2C2820",
                  }}
                >
                  <MapPin size={16} strokeWidth={2.25} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                    {s.address && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#6B6560",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.address}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#6B6560", fontWeight: 600 }}>
                    {formatDistance(s)}
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default StoresExpandedSheet;
